import React from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import InovxLoader from '@shared/global/InovxLoader';
import LoginPage from './LoginPage';
import {
    clearPlatformSession,
    ensurePlatformAccessTokenReady,
    getCurrentPlatformSession,
    PlatformSessionFailure,
    PlatformSessionNoAccessError,
    PlatformSessionRegistrationProfile,
} from './platformSession';
import SelfRegistrationPage from './SelfRegistrationPage';
import ExternalLoginErrorPage from './ExternalLoginErrorPage';
import { getFrontendConfig } from '../config/frontendConfig';

interface AuthGuardProps {
    children: React.ReactNode;
    loadingFallback?: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children, loadingFallback }) => {
    const isAuthenticated = useIsAuthenticated();
    const { accounts, instance, inProgress } = useMsal();
    const [sessionState, setSessionState] = React.useState<'loading' | 'login' | 'ready' | 'registration' | 'external-login-error' | 'error'>('loading');
    const [registrationProfile, setRegistrationProfile] = React.useState<PlatformSessionRegistrationProfile | null>(null);
    const [externalLoginError, setExternalLoginError] = React.useState<PlatformSessionFailure | null>(null);

    const startupFallback = loadingFallback ?? <div>{InovxLoader.t('common:auth.authenticating')}</div>;

    React.useEffect(() => {
        let cancelled = false;

        async function warmToken() {
            if (getCurrentPlatformSession()) {
                if (!cancelled) {
                    setRegistrationProfile(null);
                    setExternalLoginError(null);
                    setSessionState('ready');
                }
                return;
            }

            if (inProgress !== InteractionStatus.None) {
                if (!cancelled) {
                    setRegistrationProfile(null);
                    setExternalLoginError(null);
                    setSessionState('loading');
                }
                return;
            }

            if (!isAuthenticated) {
                if (!cancelled) {
                    setRegistrationProfile(null);
                    setExternalLoginError(null);
                    setSessionState('login');
                }
                return;
            }

            try {
                const accessToken = await ensurePlatformAccessTokenReady();
                if (!cancelled) {
                    setRegistrationProfile(null);
                    setExternalLoginError(null);
                    setSessionState(accessToken ? 'ready' : 'loading');
                }
            } catch (error) {
                if (!cancelled) {
                    if (error instanceof PlatformSessionNoAccessError) {
                        if (error.response.Registration) {
                            setExternalLoginError(null);
                            setRegistrationProfile(error.response.Registration);
                            setSessionState('registration');
                        } else {
                            setRegistrationProfile(null);
                            setExternalLoginError(error.response);
                            setSessionState('external-login-error');
                        }
                    } else {
                        console.warn('Failed to establish platform session before app init:', error);
                        setRegistrationProfile(null);
                        setExternalLoginError(null);
                        setSessionState('error');
                    }
                }
            }
        }

        void warmToken();

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, inProgress]);

    const handleExternalLoginErrorContinue = async (externalLogout: boolean) => {
        const basePath = getFrontendConfig().urls.basePath;
        const postLogoutRedirectUri = new URL(basePath, window.location.origin).toString();

        clearPlatformSession();
        setRegistrationProfile(null);
        setExternalLoginError(null);

        if (externalLogout) {
            await instance.logoutRedirect({
                postLogoutRedirectUri,
            });

            return;
        }

        await instance.clearCache();
        window.location.href = basePath;
    };

    if (inProgress !== InteractionStatus.None) {
        return <>{startupFallback}</>;
    }

    if (sessionState === 'login') {
        return <LoginPage onAuthenticated={() => setSessionState('ready')} />;
    }

    if (sessionState === 'loading') {
        return <>{startupFallback}</>;
    }

    if (sessionState === 'registration' && registrationProfile) {
        return (
            <SelfRegistrationPage
                registration={registrationProfile}
                onRegistered={() => {
                    setRegistrationProfile(null);
                    setSessionState('ready');
                }}
            />
        );
    }

    if (sessionState === 'external-login-error' && externalLoginError) {
        return (
            <ExternalLoginErrorPage
                failure={externalLoginError}
                username={accounts[0]?.username || accounts[0]?.name || ''}
                onContinue={handleExternalLoginErrorContinue}
            />
        );
    }

    if (sessionState === 'error') {
        return <div>{InovxLoader.t('common:auth.platformSessionFailed')}</div>;
    }

    return <>{children}</>;
};

export default AuthGuard;
